import { decryptUrlData } from "../../utils/encryption/CryptoService";

export const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

export const COPILOT_STORAGE_KEY = "molassist_threads_v1";

export const WORKFLOW_LABELS = {
  create_job_opening: "Create job opening",
  update_candidate_status: "Update status",
  find_interview_slots: "Find interview slots",
  create_candidate_from_resume: "Create candidate from resume",
  search_candidates: "Search candidates",
};

export const COPILOT_WELCOME =
  "Good day. I’m **MolAssist**, your recruitment assistant. I can help you find candidates, review job openings, and highlight what needs attention. Type **help** to see everything I can do.";

export const COPILOT_HELP_MENU =
  "How may I help you?\n\n" +
  "I'm **MolAssist**. Pick a suggestion or type in your own words.\n\n" +
  "**Candidates**\n" +
  "• Search candidate / Do we have {name}?\n" +
  "• How many candidates registered today?\n" +
  "• Show candidates registered today (then **next** for more)\n" +
  "• Find incomplete profiles\n" +
  "• Find candidates with missing HR\n" +
  "• Find candidates not connected with jobs\n" +
  "• Find candidates with no experience\n" +
  "• Show shortlist\n" +
  "• Find best matching jobs for this candidate\n" +
  "• What filters exist for candidates?\n\n" +
  "**Jobs**\n" +
  "• Search job openings\n" +
  "• Show published job openings\n" +
  "• Which job openings have no interview workflow?\n" +
  "• Create a job opening for Accountant (opens the form — nothing is saved until you Save)\n\n" +
  "**Workflows**\n" +
  "• Which interview workflows are unused?\n" +
  "• Where is workflow {name} used?\n\n" +
  "**Attention and reports**\n" +
  "• What needs attention?\n" +
  "• Show candidates waiting for feedback\n" +
  "• Which jobs have no applicants?\n" +
  "• Pending interviews today\n" +
  "• Show follow-ups due today\n" +
  "• Stuck in stage more than 7 days\n" +
  "• Recruitment report for today\n" +
  "• Export recruitment report for today (Excel download)\n" +
  "• How many exports are supported?\n" +
  "• What can you do?\n" +
  "• Type `/export` for registered export kinds; `@#J` for published openings\n\n" +
  "**Status and interviews**\n" +
  "• Add interaction for {name}\n" +
  "• Update status for {name} to new\n" +
  "• Reject {name}\n" +
  "• Schedule interview for {name}\n" +
  "• Find interview slot for {interviewer} (lists only — does not book)\n\n" +
  "**Resume**\n" +
  "• Read this resume\n" +
  "• Read this resume and create a candidate\n\n" +
  "**Other**\n" +
  "• How many skills are available?\n" +
  "• Email shortlist (type `@#J` to pick a job — copy only, does not send)\n" +
  "• Generate interview questions (opens Question Bank)\n" +
  "• Summarize candidate {name} (opens profile + Candidate Summary)\n\n" +
  "**Session**\n" +
  "• Clear chat (wipe all conversations in this browser session)\n\n" +
  "Ask in your own words. I never invent names or counts. Status and create actions wait for Yes.";

export const RECRUITMENT_STARTERS = [
  "What needs attention?",
  "How many skills are available?",
  "Find incomplete profiles",
  "Find candidates with missing HR",
  "Show published job openings",
  "Recruitment report for today",
  "Export recruitment report for today",
  "Add interaction for",
  "Find interview slot for",
  "Find candidates not connected with jobs",
  "Find candidates with no experience",
  "Show candidates waiting for feedback",
  "Show follow-ups due today",
  "Create a job opening for Accountant",
  "Update status for candidate to new",
];

export const RESUME_STARTERS = [
  "Read this resume and create a candidate",
  "Review this resume",
  "Find best matching jobs for this candidate",
];

export const getStartersForModule = (module, jobOpeningId) => {
  if (module === "Resume") return RESUME_STARTERS;
  const id = Number(jobOpeningId);
  if (id > 0) {
    return [
      ...RECRUITMENT_STARTERS.slice(0, 5),
      `Email shortlist for opening #${id}`,
      ...RECRUITMENT_STARTERS.slice(5),
    ];
  }
  return RECRUITMENT_STARTERS;
};

/** Turn a list line or chip label into "select applicant 210" when an id is present. */
export const applicantSelectPrompt = (raw) => {
  const t = String(raw || "")
    .trim()
    .replace(/\*\*/g, "");
  const select = /^(?:select\s+)?(?:applicant|candidate)\s+#?\s*(\d{1,9})\b/i.exec(t);
  if (select) return `select applicant ${select[1]}`;
  const hashStart = /^#\s*(\d{1,9})\b/.exec(t);
  if (hashStart) return `select applicant ${hashStart[1]}`;
  const paren = /\(#\s*(\d{1,9})\s*\)$/.exec(t);
  if (paren) return `select applicant ${paren[1]}`;
  return null;
};

const IMMEDIATE_PROMPTS = new Set([
  "y",
  "ok",
  "okay",
  "stop",
  "abort",
  "done",
  "try another name",
  "email:",
  "phone:",
  "name:",
  "as interviewer",
  "as candidate",
]);

/** Yes / Cancel / pick-an-id chips still send immediately. */
export const isImmediatePrompt = (raw) => {
  if (applicantSelectPrompt(raw)) return true;
  const t = String(raw || "").trim();
  const normalized = t.toLowerCase();
  return (
    IMMEDIATE_PROMPTS.has(normalized) ||
    /^(yes|confirm|cancel)\b/i.test(t) ||
    /^(save|submit)(\s+interaction)?$/i.test(t) ||
    /^select\s+(applicant|interviewer|slot)\s+/i.test(t) ||
    /^refresh\s+interaction\s+form/i.test(t)
  );
};

/** Put a menu/suggestion line into the composer so the user can finish it. */
export const draftPromptFromSuggestion = (raw) => {
  const pick = applicantSelectPrompt(raw);
  if (pick) return pick;
  let t = String(raw || "").trim();
  t = t.replace(/\s*\([^)]*\)\s*$/g, "").trim();
  if (t.includes(" / ")) t = t.split(" / ")[0].trim();
  t = t
    .replace(/\{[^}]+\}/g, "")
    .replace(/\s+[?.!]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (/\b(for|have)$/i.test(t)) t += " ";
  return t;
};

/** True while an @entity token still needs a dropdown selection. */
export const hasUnresolvedEntityToken = (raw) =>
  /(^|\s)@(?:#)?[a-z]*(?:\s|$)/i.test(
    String(raw || "").trim()
  );

/** Complete suggestions send immediately; templates with placeholders stay in the composer. */
export const isCompleteSuggestion = (raw) => {
  if (applicantSelectPrompt(raw)) return true;
  const original = String(raw || "").trim();
  if (/\{[^}]+\}/.test(original)) return false;
  const drafted = draftPromptFromSuggestion(raw);
  if (/\b(for|have)\s*$/i.test(drafted)) return false;
  // Bare @entity tokens still need the dropdown pick (e.g. Export applicants @#S).
  if (hasUnresolvedEntityToken(drafted)) return false;
  return Boolean(String(drafted || "").trim());
};

export const createThreadId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `thread-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const deriveThreadTitle = (firstMessage, pageContext) => {
  const msg = String(firstMessage || "").trim();
  if (msg.length >= 3) return msg.length > 28 ? `${msg.slice(0, 28)}…` : msg;
  if (pageContext?.entityLabel) return pageContext.entityLabel;
  if (pageContext?.entityType) return pageContext.entityType;
  return "New chat";
};

const tryDecryptId = (raw) => {
  if (raw == null || raw === "") return null;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
  try {
    const decrypted = decryptUrlData(String(raw));
    const id = Number(decrypted);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

export const formatWorkingWithLabel = (pageContext, jobOpeningIdParam) => {
  const label = pageContext?.entityLabel;
  if (label) return `Working with: ${label}`;
  const type = pageContext?.entityType;
  if (type === "JobOpening" || jobOpeningIdParam) {
    return "Working with: Job Opening";
  }
  if (type === "Applicant") return "Working with: Applicant";
  if (type === "ApplicantResume") return "Working with: Parsed Resume";
  if (type) return `Working with: ${type}`;
  return null;
};

export const buildPageContext = (location) => {
  const path = location?.pathname || "";
  const search = location?.search || "";
  const params = new URLSearchParams(search);
  const segments = path.split("/").filter(Boolean);
  const ctx = {
    route: path,
    page: segments[0] || "home",
    module: "Recruitment",
    entityType: null,
    entityId: null,
    entityLabel: null,
    jobOpeningId: null,
  };

  if (path.includes("applicant-details") && segments[1]) {
    ctx.entityType = "Applicant";
    ctx.entityId = tryDecryptId(segments[1]);
    ctx.page = "ApplicantDetails";
  } else if (path.includes("modify-applicant-details") && segments[1]) {
    ctx.entityType = "ApplicantResume";
    ctx.page = "ParsedResume";
    ctx.module = "Resume";
  } else if (path.includes("job-opening")) {
    ctx.page = "JobOpening";
    ctx.entityType = "JobOpening";
    const rawId =
      params.get("editId") || params.get("jobOpeningId") || params.get("id");
    ctx.entityId = tryDecryptId(rawId);
    ctx.jobOpeningId = ctx.entityId;
  } else if (path.includes("batch-process") || path.includes("parsed-resumes")) {
    ctx.module = "Resume";
    ctx.page = "ResumeProcessing";
  }

  return ctx;
};

export const normalizeAssistantMessage = (data) => ({
  role: "assistant",
  content: pick(data, "replyText", "ReplyText") || "No reply returned.",
  toolsRan: pick(data, "toolsRan", "ToolsRan") || [],
  suggestedActions: pick(data, "suggestedActions", "SuggestedActions") || [],
  toolResults: pick(data, "toolResults", "ToolResults") || [],
  blocks: pick(data, "blocks", "Blocks") || [],
  draftEmailText: pick(data, "draftEmailText", "DraftEmailText") || null,
  draftEmailSubject: pick(data, "draftEmailSubject", "DraftEmailSubject") || null,
  draftEmailRecipients: pick(data, "draftEmailRecipients", "DraftEmailRecipients") || [],
  workflow: pick(data, "workflow", "Workflow") || null,
  listSession: pick(data, "listSession", "ListSession") || null,
  pendingEntityQuery: pick(data, "pendingEntityQuery", "PendingEntityQuery") || null,
  lastListFilters: pick(data, "lastListFilters", "LastListFilters") || null,
  lastMetricCode: pick(data, "lastMetricCode", "LastMetricCode") || null,
  pendingConfirmation: pick(data, "pendingConfirmation", "PendingConfirmation") || null,
  aiExecutionLogId: pick(data, "aiExecutionLogId", "AIExecutionLogId") || null,
  errorText: pick(data, "errorText", "ErrorText") || null,
  isError: false,
});

const STRUCTURED_BLOCK_TYPES = new Set([
  "candidatecard",
  "jobopeningcard",
  "interviewercard",
  "slotcard",
  "resumepreview",
  "metricsummary",
  "confirmation",
  "interactionform",
  "table",
]);

export const getBlockType = (block) =>
  String(pick(block, "type", "Type") || "text").toLowerCase();

export const hasStructuredBlocks = (blocks = []) =>
  blocks.some((b) => STRUCTURED_BLOCK_TYPES.has(getBlockType(b)));

const countBlocksByType = (type, blocks = []) =>
  blocks.filter((block) => getBlockType(block) === type).length;

const getToolResultCount = (toolResults = []) =>
  toolResults.reduce(
    (sum, result) => sum + (Number(pick(result, "count", "Count")) || 0),
    0
  );

const getResultTotal = (message) => {
  const totalFromSession =
    Number(pick(message?.listSession, "totalCount", "TotalCount")) || 0;
  if (totalFromSession > 0) return totalFromSession;

  const totalFromTools = getToolResultCount(message?.toolResults);
  if (totalFromTools > 0) return totalFromTools;

  return 0;
};

const pluralize = (count, singular, plural = `${singular}s`) =>
  count === 1 ? singular : plural;

const getCardSummary = ({ count, total, label, pluralLabel }) => {
  if (count <= 0) return "";

  const resultCount = total > 0 ? total : count;
  return `Found ${resultCount} ${pluralize(resultCount, label, pluralLabel)}.`;
};

const shouldHideVerboseCardContent = (content) =>
  content.length > 120 || (content.includes("**") && content.split("\n").length > 4);

const getStructuredBlockSummary = (message, blocks) => {
  const total = getResultTotal(message);
  const summaries = [
    {
      count: countBlocksByType("jobopeningcard", blocks),
      label: "published job opening",
    },
    {
      count: countBlocksByType("candidatecard", blocks),
      label: "candidate",
    },
    {
      count: countBlocksByType("interviewercard", blocks),
      label: "interviewer",
    },
    {
      count: countBlocksByType("slotcard", blocks),
      label: "interview slot",
      pluralLabel: "interview slots",
    },
  ];

  return summaries
    .map((summary) => getCardSummary({ ...summary, total }))
    .find(Boolean) || "Here are the results.";
};

/** When cards are shown, hide verbose markdown list replies from the bubble. */
export const getAssistantDisplayContent = (message) => {
  const content = String(message?.content || "").trim();
  const blocks = message?.blocks || [];
  const firstText = String(
    pick(
      blocks.find((b) => getBlockType(b) === "text"),
      "markdown",
      "Markdown"
    ) || ""
  ).trim();
  if (firstText && firstText === content && !hasStructuredBlocks(blocks)) {
    return "";
  }
  if (!hasStructuredBlocks(blocks)) return content;

  const structuredCount = blocks.filter((b) =>
    STRUCTURED_BLOCK_TYPES.has(getBlockType(b))
  ).length;

  if (structuredCount === 0) return content;

  // Keep short summaries; hide long duplicate lists when cards exist.
  if (shouldHideVerboseCardContent(content)) {
    return getStructuredBlockSummary(message, blocks);
  }

  return content;
};

const GROUPABLE_BLOCK_TYPES = new Set([
  "jobopeningcard",
  "candidatecard",
  "interviewercard",
  "slotcard",
]);

const resetActiveGroups = (activeGroups, types) => {
  types.forEach((type) => {
    activeGroups[type] = null;
  });
};

const resetGroupsForType = (activeGroups, type) => {
  if (type === "jobopeningcard") {
    resetActiveGroups(activeGroups, ["candidatecard", "interviewercard", "slotcard"]);
    return;
  }

  if (type === "candidatecard") {
    resetActiveGroups(activeGroups, ["jobopeningcard", "interviewercard", "slotcard"]);
    return;
  }

  if (type === "interviewercard") {
    resetActiveGroups(activeGroups, ["jobopeningcard", "candidatecard", "slotcard"]);
    return;
  }

  resetActiveGroups(activeGroups, ["jobopeningcard", "candidatecard", "interviewercard"]);
};

const getBlockGroupKey = (type, block, index) =>
  `${type}-${pick(block, "id", "Id", "applicantId", "ApplicantId", "jobOpeningId", "JobOpeningId") || index}`;

const appendGroupedBlock = ({ groups, activeGroups, block, type, index }) => {
  resetGroupsForType(activeGroups, type);

  if (!activeGroups[type]) {
    activeGroups[type] = {
      kind: type,
      blocks: [],
      key: getBlockGroupKey(type, block, index),
    };
    groups.push(activeGroups[type]);
  }

  activeGroups[type].blocks.push(block);
};

export const groupBlocksForDisplay = (blocks = []) => {
  const groups = [];
  const activeGroups = {};

  blocks.forEach((block, index) => {
    const type = getBlockType(block);

    if (GROUPABLE_BLOCK_TYPES.has(type)) {
      appendGroupedBlock({ groups, activeGroups, block, type, index });
      return;
    }

    resetActiveGroups(activeGroups, [...GROUPABLE_BLOCK_TYPES]);
    groups.push({ kind: type, blocks: [block], key: getBlockGroupKey(type, block, index) });
  });

  return groups;
};

export const filterDuplicateActions = (actions = [], blocks = []) => {
  if (!hasStructuredBlocks(blocks)) return actions;

  const hasJobCards = blocks.some((b) => getBlockType(b) === "jobopeningcard");
  if (!hasJobCards) return actions;

  return actions.filter((a) => {
    const label = String(pick(a, "label", "Label") || "").toLowerCase();
    return !label.includes("browse job");
  });
};

/** Prefer thread.workflow; only the latest assistant turn may restore it — never revive an older confirm after a completed reply. */
export const resolveActiveWorkflow = (thread) => {
  if (thread?.workflow) return thread.workflow;

  const messages = thread?.messages || [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.role !== "assistant") continue;
    const wf = msg.workflow;
    if (wf && (wf.type || wf.Type)) return wf;
    return null;
  }

  return null;
};

/** Prefer thread.listSession; fall back to the latest assistant turn that carried one. */
export const resolveActiveListSession = (thread) => {
  if (thread?.listSession) return thread.listSession;

  const messages = thread?.messages || [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.role !== "assistant") continue;
    if (msg.listSession) return msg.listSession;
    // Stop at the latest assistant reply even if it cleared the session.
    return null;
  }

  return null;
};

export const buildChatContext = ({
  pageContext,
  workflow,
  attachments,
  jobOpeningIdParam,
  listSession,
  pendingEntityQuery,
  lastListFilters,
  lastMetricCode,
}) => {
  const context = {
    module: pageContext?.module || "Recruitment",
    page: pageContext?.page,
    entityType: pageContext?.entityType,
    entityLabel: pageContext?.entityLabel,
    entityId: pageContext?.entityId,
  };

  const jobId = jobOpeningIdParam || pageContext?.jobOpeningId;
  if (jobId) {
    const id = Number(jobId);
    if (id > 0) context.jobOpeningId = id;
  }

  if (workflow) context.workflow = workflow;
  if (attachments?.length) context.attachments = attachments;
  if (listSession) context.listSession = listSession;
  if (pendingEntityQuery) context.pendingEntityQuery = pendingEntityQuery;
  if (lastListFilters && Object.keys(lastListFilters).length)
    context.lastListFilters = lastListFilters;
  if (lastMetricCode) context.lastMetricCode = lastMetricCode;

  return context;
};
